import { AppProps } from 'next/app';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next';
import i18nConfig from '../next-i18next.config';
import Prefetch from '../components/Prefetch';
import { DEBUG, IS_CLIENT_SIDE } from '../config';
import { FeatureFlags } from '../services/featureFlags';
import { CommonPageProps } from '../src/getServerSideProps';

// TODO SSR hack for missing Element in Server
if (typeof Element === 'undefined') {
	(global as any).Element = () => {};
}

// TODO SSR hack for missing navigator in Server
if (typeof navigator === 'undefined') {
	(global as any).navigator = {};
}

(global as any).I18N_AVAILABLE_LANGUAGES = createAvailableLanguages();

// TODO SSR hack to hide multiple react versions error in dev
if (DEBUG) {
	// eslint-disable-next-line no-console
	const originalConsoleError = console.error;
	try {
		// eslint-disable-next-line no-console
		console.error = (msg, ...rest) => {
			if (typeof msg === 'string') {
				const isKnownReactHookError =
					msg.includes('Warning: Invalid hook call.') &&
					msg.includes('https://reactjs.org/link/invalid-hook-call');
				if (isKnownReactHookError) {
					return;
				}

				originalConsoleError(msg, ...rest);
			}
		};
	} catch (error) {}
}

const MyApp = ({ Component, pageProps }: AppProps<CommonPageProps>) => {
	// TODO SSR We need the div with app as an id so react-modal does not crash
	const title = pageProps.title || 'StreamYard';
	const description =
		pageProps.description ||
		'Engage your Facebook, or YouTube, Live audience with interviews and shows; all the tools you need for professional shows right in your browser.';

	const { nonce, featureFlags } = pageProps;

	const REDUX_DATA = buildReduxData({ featureFlags });

	if (!IS_CLIENT_SIDE) {
		(global as any).ssr = { featureFlags };
	}

	return (
		<>
			<Head>
				{REDUX_DATA && (
					<script
						id="feature-flags"
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: `window.REDUX_DATA = ${REDUX_DATA}`,
						}}
					/>
				)}
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta name="og:description" content={description} />
				<meta name="twitter:description" content={description} />
				<meta name="twitter:image:alt" content={description} />
			</Head>
			<Prefetch />
			<div id="app">
				<Component {...pageProps} />
			</div>
		</>
	);
};

function buildReduxData({ featureFlags }: { featureFlags: FeatureFlags }) {
	try {
		return JSON.stringify({ features: featureFlags });
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error building redux data', error);
		return null;
	}
}

function createAvailableLanguages(): any {
	const { i18n, localeDisplayNames } = i18nConfig;
	const { locales } = i18n;

	return locales.map((localeCode) => {
		return {
			code: localeCode,
			label: localeDisplayNames[localeCode as keyof typeof localeDisplayNames],
		};
	});
}

export default appWithTranslation(MyApp);
